import { Dispatch, SetStateAction, FC, type JSX } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { NotificationBox, Stack, Text, Bleed, Box, Divider } from '@eneco/sparky';

import {
  OxxioElectricityRedeliverySectionView,
  OxxioElectricityVariableSectionView,
  OxxioExplanationSectionView,
  OxxioFixedCostsSectionView,
  OxxioGasVariableSectionView,
  OxxioGovernmentLeviesSectionView,
  OxxioSubtotalSectionView,
  OxxioTotalsSectionView,
} from './oxxio/OxxioPriceDetailsSections';
import type { FieldsUnionOxxio } from './price-details-overview-oxxio.types';
import { usePriceDetailsOverviewOxxio } from './usePriceDetailsOverviewOxxio';
import { FlowConsumerContextEnergy } from '../../../context';
import { PriceDetailsOverviewRedeliveryNotification } from '../../../shared/components/PriceDetailsOverviewRedeliveryNotification/PriceDetailsOverviewRedeliveryNotification';
import type { Offer } from '../../../shared/types';

export type { FieldsUnionOxxio };

interface Props {
  flowContext: FlowConsumerContextEnergy;
  textualData?: FieldsUnionOxxio;
  offer?: Offer;
  isOpen?: boolean;
  setOpen?: Dispatch<SetStateAction<boolean>>;
}

export const PriceDetailsOverviewOxxio: FC<Props> = ({
  flowContext,
  textualData,
  offer,
  isOpen,
  setOpen,
}): JSX.Element => {
  const {
    usages,
    shouldShowNotificationsPanel,
    shouldShowDynamicPricingNotification,
    shouldShowVariablePricingNotification,
    electricityVariableSection,
    electricityRedeliverySection,
    gasVariableSection,
    electricityGovernmentLeviesSection,
    gasGovernmentLeviesSection,
    electricityFixedCostsSection,
    gasFixedCostsSection,
    subtotalValue,
    subtotalLabel,
    totalsSection,
    productDetailsTariffSplitExplanation,
    hasFutureRate,
  } = usePriceDetailsOverviewOxxio({ flowContext, offer, textualData });

  return (
    <Stack direction="column" gap="4">
      <Text size="BodyS">
        {shouldShowNotificationsPanel && (
          <>
            <Bleed horizontal="3">
              <Divider />
            </Bleed>

            <Bleed horizontal="6">
              <Box backgroundColor="backgroundPrimary" padding="6">
                <PriceDetailsOverviewRedeliveryNotification
                  flowContext={flowContext}
                  usageData={usages}
                  redeliveryMoreNotificationTextualData={textualData?.costDetails?.redeliveryMoreNotification}
                  redeliveryLessNotificationTextualData={textualData?.costDetails?.redeliveryLessNotification}
                />

                {shouldShowDynamicPricingNotification && (
                  <NotificationBox
                    isAlert={false}
                    variant={textualData?.costDetails?.dynamicPricingNotification?.value?.variant}
                    title={textualData?.costDetails?.dynamicPricingNotification?.value?.title}
                    text={<RichText html={textualData?.costDetails?.dynamicPricingNotification?.value?.content} />}
                  />
                )}

                {shouldShowVariablePricingNotification && (
                  <NotificationBox
                    isAlert={false}
                    variant={textualData?.productData?.variableProductNotification?.value?.variant}
                    title={textualData?.productData?.variableProductNotification?.value?.title}
                    text={<RichText html={textualData?.productData?.variableProductNotification?.value?.content} />}
                  />
                )}
              </Box>
            </Bleed>
          </>
        )}

        <Stack direction="column" gap="3">
          <Bleed horizontal="3">
            <Divider emphasis="high" />
          </Bleed>

          {electricityVariableSection && (
            <OxxioElectricityVariableSectionView section={electricityVariableSection} />
          )}

          {electricityRedeliverySection && (
            <OxxioElectricityRedeliverySectionView section={electricityRedeliverySection} />
          )}

          {gasVariableSection && <OxxioGasVariableSectionView section={gasVariableSection} />}

          <OxxioGovernmentLeviesSectionView section={electricityGovernmentLeviesSection} />

          {gasGovernmentLeviesSection && (
            <OxxioGovernmentLeviesSectionView section={gasGovernmentLeviesSection} />
          )}

          <OxxioFixedCostsSectionView section={electricityFixedCostsSection} />

          {gasFixedCostsSection && (
            <OxxioFixedCostsSectionView section={gasFixedCostsSection} showLeadingDivider={false} />
          )}

          <OxxioSubtotalSectionView label={subtotalLabel} value={subtotalValue} />

          <OxxioTotalsSectionView section={totalsSection} />

          <OxxioExplanationSectionView
            flowContext={flowContext}
            offer={offer}
            textualData={textualData}
            isOpen={isOpen}
            setOpen={setOpen}
            hasFutureRate={hasFutureRate}
            productDetailsTariffSplitExplanation={productDetailsTariffSplitExplanation}
          />
        </Stack>
      </Text>
    </Stack>
  );
};
